import { BasketFullParams, BasketProductFullParams, FindBasketProductsResponse } from 'services/basket/interfaces';
import { OrderRevisionContextParams, RoleAlertConfig } from './interfaces';
import { onPaymentFormChange, onCheckoutPageLoad, onCloseOrder } from 'shared/gtm';

export const TEMP_COLOR_REMAPPING = {
	textLink: '--text-link',
	textPrimary: '--text-primary',
	textSecondary: '--text-secondary',
	semanticBaseSuccess: '--semantic-success-base',
	semanticSuccessText: '--semantic-success-text',
};

export const handleGetOrderTotals = (
	selectedIds: string[],
	baskets: FindBasketProductsResponse['baskets'],
): OrderRevisionContextParams['totals'] => {
	return baskets.reduce(
		(acc, { id, totalPrice, totalPricePromotion, totalTaxSubstitution, finalTotalPrice, businessUnitName }) => {
			if (!selectedIds?.includes(id)) return acc;

			if (businessUnitName) {
				const BUSlugName = businessUnitName.slugify() as 'marcas' | 'neo-quimica';

				acc.BUValues[BUSlugName] = acc.BUValues[BUSlugName] + totalPricePromotion;
			}

			acc.summary = {
				brute: acc.summary.brute + totalPrice,
				discount: acc.summary.discount + totalPrice - totalPricePromotion,
				tax: acc.summary.tax + (totalTaxSubstitution || 0),
				liquid: acc.summary.liquid + finalTotalPrice,
			};

			return acc;
		},
		{
			BUValues: {
				marcas: 0,
				'neo-quimica': 0,
			},
			summary: {
				brute: 0,
				discount: 0,
				tax: 0,
				liquid: 0,
			},
		},
	);
};

export const GTMEventTrigger = {
	onPageLoad({
		baskets,
		totalPricePromotion,
		user_engaged_boni,
	}: {
		totalPricePromotion: number;
		baskets: BasketFullParams[];
		user_engaged_boni: boolean;
	}) {
		onCheckoutPageLoad({
			totalPricePromotion: Number(totalPricePromotion),
			user_engaged_boni: user_engaged_boni,
			basketProductsData: getBasketProductsData(baskets),
		});
	},
	onAfterCloseOrder({
		posName,
		baskets,
		posCNPJ,
		basketGroupCode,
		closedBasketIds,
		totalPricePromotion,
		totalTaxSubstitution,
		hasCombo,
		totalBoniPoints,
		isEngagedBoni,
		customerName,
		customerEmail,
		customerPhoneNumber,
		customerPhoneConfirmed,
		posStreet,
		posCity,
		posRegion,
		posPostalCode,
	}: {
		posName: string;
		posCNPJ: string;
		baskets: BasketFullParams[];
		basketGroupCode: string;
		closedBasketIds: string[];
		totalPricePromotion: number;
		totalTaxSubstitution: number;
		hasCombo: boolean;
		totalBoniPoints: number;
		isEngagedBoni: boolean;
		customerName?: string;
		customerEmail?: string;
		customerPhoneNumber?: string | null;
		customerPhoneConfirmed?: boolean;
		posStreet?: string;
		posCity?: string;
		posRegion?: string;
		posPostalCode?: string;
	}) {
		const distributorsName: string[] = [];

		const closedOrderBasketsData = baskets.reduce((acc, basket) => {
			if (!closedBasketIds.includes(basket.id)) return acc;
			const sourceType: keyof typeof basket = basket.businessUnitName ? 'businessUnitName' : 'industryName';

			distributorsName.push(basket.distributorName);

			acc.push(
				basket.products.map((product) => ({
					...product,
					affiliation: basket.distributorName,
					[sourceType]: basket[sourceType],
					minimum_value: basket.minimumValueForSale,
				})),
			);

			return acc;
		}, [] as BasketProductFullParams[][]);

		onCloseOrder({
			posCNPJ,
			posName,
			basketGroupCode,
			distributorsName,
			totalPricePromotion,
			totalTaxSubstitution,
			closedOrderBasketsData,
			hasCombo,
			totalBoniPoints,
			isEngagedBoni,
			customerName,
			customerEmail,
			customerPhoneNumber,
			customerPhoneConfirmed,
			posStreet,
			posCity,
			posRegion,
			posPostalCode,
		});
	},
	onPaymentFormChange({
		baskets,
		paymentFormLabel,
		totalPricePromotion,
	}: {
		baskets: BasketFullParams[];
		paymentFormLabel: string;
		totalPricePromotion: number;
	}) {
		const paymentFormBasketsData = getBasketProductsData(baskets);

		onPaymentFormChange({
			totalPricePromotion,
			paymentFormBasketsData,
			paymentFormLabel,
		});
	},
};

export const roleAlertConfig: Record<string, RoleAlertConfig> = {
	CONSULTANT: {
		alertKey: 'ExclusiveOrderRevision',
		alertDays: 45,
		modalViews: 3,
		id: (userId) => `Consultant:${userId}`,
	},
	SUPERVISOR: {
		alertKey: 'ExclusiveOrderRevision',
		alertDays: 45,
		modalViews: 3,
		id: (userId) => `Supervisor:${userId}`,
	},
	DEFAULT: {
		alertKey: '',
		alertDays: 0,
		modalViews: 0,
		id: () => '',
	},
};

export const getBasketProductsData = (baskets: BasketFullParams[]): BasketProductFullParams[][] =>
	baskets.map((basket) =>
		basket.products.map((product) => {
			const sourceType: keyof typeof basket = basket.businessUnitName ? 'businessUnitName' : 'industryName';

			return {
				...product,
				affiliation: basket.distributorName,
				[sourceType]: basket[sourceType],
			};
		}),
	);
